/**
 * Select – Popover-based replacement for Radix Select.
 *
 * Drop-in API compatible: Select, SelectTrigger, SelectValue, SelectContent,
 * SelectItem, SelectGroup, SelectLabel, SelectSeparator.
 *
 * Uses Popover internally so the dropdown is always a styled popover panel
 * (never a native OS popup).
 */
import * as React from 'react';
import { Check, ChevronDown } from 'lucide-react';

import { cn } from '../../lib/utils';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './popover';

/* ------------------------------------------------------------------ */
/*  Helpers                                                           */
/* ------------------------------------------------------------------ */

/** Marker so scanForItems can reliably identify SelectItem elements. */
const SELECT_ITEM_TYPE = Symbol.for('SelectItem');

/**
 * Recursively walk a React children tree and extract value→children pairs
 * from every SelectItem element found.  Works even when items are wrapped
 * in Tooltip, SelectGroup, Fragment, etc.
 */
function scanForItems(
  children: React.ReactNode,
  registry: Map<string, React.ReactNode>,
) {
  React.Children.forEach(children, (child) => {
    if (!React.isValidElement(child)) return;

    // Check our marker
    if (
      (child.type as unknown as Record<string, unknown>)?.__SELECT_ITEM === SELECT_ITEM_TYPE &&
      'value' in child.props
    ) {
      const itemProps = child.props as { value: string; textValue?: string; children?: React.ReactNode };
      // Use textValue for trigger display if provided, otherwise fall back to children
      registry.set(
        itemProps.value,
        itemProps.textValue ?? itemProps.children,
      );
    }

    // Recurse into any wrapper (Tooltip, SelectGroup, Fragment, …)
    if ((child.props as { children?: React.ReactNode }).children) {
      scanForItems(
        (child.props as { children?: React.ReactNode }).children,
        registry,
      );
    }
  });
}

/* ------------------------------------------------------------------ */
/*  Context                                                           */
/* ------------------------------------------------------------------ */

interface SelectContextValue {
  value: string;
  onValueChange: (value: string) => void;
  open: boolean;
  setOpen: (open: boolean) => void;
  disabled?: boolean;
  itemsRef: React.MutableRefObject<Map<string, React.ReactNode>>;
}

const SelectContext = React.createContext<SelectContextValue | null>(null);

function useSelectContext() {
  const ctx = React.useContext(SelectContext);
  if (!ctx) throw new Error('Select components must be used within <Select>');
  return ctx;
}

/* ------------------------------------------------------------------ */
/*  Select (root)                                                     */
/* ------------------------------------------------------------------ */

interface SelectProps {
  value?: string;
  defaultValue?: string;
  /** Method syntax keeps bivariant checking (matches Radix Select behaviour) */
  onValueChange?(value: string): void;
  children: React.ReactNode;
  disabled?: boolean;
}

function Select({
  value,
  defaultValue = '',
  onValueChange = () => {},
  children,
  disabled,
}: SelectProps) {
  // Support both controlled (value) and uncontrolled (defaultValue) modes
  const [internalValue, setInternalValue] = React.useState(defaultValue);
  const isControlled = value !== undefined;
  const effectiveValue = isControlled ? value : internalValue;
  const effectiveOnChange = React.useCallback(
    (v: string) => {
      if (!isControlled) setInternalValue(v);
      onValueChange(v);
    },
    [isControlled, onValueChange],
  );
  const [open, setOpen] = React.useState(false);
  const itemsRef = React.useRef(new Map<string, React.ReactNode>());

  // Pre-scan children so SelectValue can display labels before the dropdown opens
  scanForItems(children, itemsRef.current);

  const effectiveSetOpen = React.useCallback(
    (v: boolean) => {
      if (!disabled) setOpen(v);
    },
    [disabled],
  );

  return (
    <SelectContext.Provider
      value={{ value: effectiveValue, onValueChange: effectiveOnChange, open, setOpen: effectiveSetOpen, disabled, itemsRef }}
    >
      <Popover open={disabled ? false : open} onOpenChange={effectiveSetOpen}>
        {children}
      </Popover>
    </SelectContext.Provider>
  );
}

/* ------------------------------------------------------------------ */
/*  SelectTrigger                                                     */
/* ------------------------------------------------------------------ */

const SelectTrigger = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ className, children, onKeyDown, ...props }, ref) => {
  const { disabled, open, setOpen } = useSelectContext();

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>) => {
      // When the dropdown is open but focus never left the trigger (e.g. an
      // empty list, so no option got focus), Escape must close only the
      // dropdown and NOT bubble to an ancestor DialogForm. Trap it here — the
      // options-focused case is handled inside SelectContent.
      if (open && e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        setOpen(false);
      }
      onKeyDown?.(e);
    },
    [open, setOpen, onKeyDown],
  );

  return (
    <PopoverTrigger asChild>
      <button
        ref={ref}
        type="button"
        role="combobox"
        disabled={disabled || props.disabled}
        onKeyDown={handleKeyDown}
        className={cn(
          'flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground hover:bg-accent transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1',
          className,
        )}
        {...props}
      >
        {children}
        <span className="flex h-4 w-4 shrink-0 items-center justify-center opacity-50">
          <ChevronDown className="h-4 w-4" />
        </span>
      </button>
    </PopoverTrigger>
  );
});
SelectTrigger.displayName = 'SelectTrigger';

/* ------------------------------------------------------------------ */
/*  SelectValue                                                       */
/* ------------------------------------------------------------------ */

function SelectValue({
  placeholder,
  children,
}: {
  placeholder?: string;
  children?: React.ReactNode;
  className?: string;
}) {
  const { value, itemsRef } = useSelectContext();

  // Explicit children always win
  // dir="auto" so the selected value keeps logical bidi order for mixed Hebrew/English labels in RTL
  if (children) return <span dir="auto">{children}</span>;

  // Auto-display: look up the item content registered by scanForItems
  const content = itemsRef.current.get(value);
  if (content !== undefined) return <span dir="auto">{content}</span>;

  // Placeholder when nothing selected
  if (placeholder && !value) {
    return <span className="text-muted-foreground">{placeholder}</span>;
  }

  return <span dir="auto">{value || placeholder || ''}</span>;
}

/* ------------------------------------------------------------------ */
/*  SelectContent                                                     */
/* ------------------------------------------------------------------ */

const SelectContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    position?: string;
    align?: 'start' | 'center' | 'end';
  }
>(({ className, children, position: _position, align = 'start', ...props }, ref) => {
  const { open, setOpen, value, onValueChange, itemsRef } = useSelectContext();
  const innerRef = React.useRef<HTMLDivElement>(null);

  // Merge forwarded ref with our internal ref
  const mergedRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      (innerRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
    },
    [ref],
  );

  // Scan the React element tree to register items for SelectValue.
  // No hidden DOM is produced – we only read element props.
  scanForItems(children, itemsRef.current);

  // Auto-focus the selected item (or first item) when the dropdown opens
  React.useEffect(() => {
    if (!open) return;
    // Use requestAnimationFrame to ensure DOM is rendered
    const raf = requestAnimationFrame(() => {
      const container = innerRef.current;
      if (!container) return;
      const items = container.querySelectorAll<HTMLElement>('[role="option"]:not([aria-disabled="true"])');
      if (items.length === 0) return;
      // Focus the selected item, or the first item
      let target: HTMLElement | null = null;
      if (value) {
        target = container.querySelector<HTMLElement>(`[role="option"][data-value="${CSS.escape(value)}"]`);
      }
      if (!target) target = items[0];
      target?.focus();
    });
    return () => cancelAnimationFrame(raf);
  }, [open, value]);

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      // Keys the open dropdown fully owns must not leak up the React tree. The
      // content is portaled, but React synthetic events still bubble through the
      // component tree — so without stopPropagation an Escape here reaches an
      // ancestor DialogForm and closes the whole dialog (and Enter could submit
      // it). preventDefault stops native behaviour; stopPropagation keeps the
      // handled key inside the Select.
      const consume = () => {
        e.preventDefault();
        e.stopPropagation();
      };

      // Escape dismisses the dropdown and must be trapped even when the list is
      // empty (no options yet) — handle it before the empty-list short-circuit,
      // otherwise an open-but-empty Select lets Escape bubble to the dialog.
      if (e.key === 'Escape') {
        consume();
        setOpen(false);
        return;
      }

      const container = innerRef.current;
      if (!container) return;
      const items = Array.from(
        container.querySelectorAll<HTMLElement>('[role="option"]:not([aria-disabled="true"])'),
      );
      if (items.length === 0) return;
      const currentIndex = items.findIndex((el) => el === document.activeElement);

      switch (e.key) {
        case 'ArrowDown': {
          consume();
          const next = currentIndex < items.length - 1 ? currentIndex + 1 : 0;
          items[next]?.focus();
          break;
        }
        case 'ArrowUp': {
          consume();
          const prev = currentIndex > 0 ? currentIndex - 1 : items.length - 1;
          items[prev]?.focus();
          break;
        }
        case 'Home': {
          consume();
          items[0]?.focus();
          break;
        }
        case 'End': {
          consume();
          items[items.length - 1]?.focus();
          break;
        }
        case 'Enter':
        case ' ': {
          consume();
          const focused = document.activeElement as HTMLElement | null;
          const itemValue = focused?.getAttribute('data-value');
          if (itemValue != null) {
            onValueChange(itemValue);
            setOpen(false);
          }
          break;
        }
      }
    },
    [onValueChange, setOpen],
  );

  if (!open) return null;

  return (
    <PopoverContent
      ref={mergedRef}
      align={align}
      role="listbox"
      className={cn('max-h-96 overflow-y-auto p-1', className)}
      style={{
        width: 'var(--radix-popover-trigger-width)',
        minWidth: '8rem',
        ...props.style,
      }}
      onOpenAutoFocus={(e) => e.preventDefault()}
      onKeyDown={handleKeyDown}
      {...props}
    >
      {children}
    </PopoverContent>
  );
});
SelectContent.displayName = 'SelectContent';

/* ------------------------------------------------------------------ */
/*  SelectItem                                                        */
/* ------------------------------------------------------------------ */

const SelectItem = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { value: string; disabled?: boolean; textValue?: string }
>(({ value: itemValue, className, children, disabled, textValue: _textValue, ...props }, ref) => {
  const { value, onValueChange, setOpen } = useSelectContext();
  const isSelected = value === itemValue;

  return (
    <div
      ref={ref}
      role="option"
      aria-selected={isSelected}
      aria-disabled={disabled || undefined}
      data-value={itemValue}
      tabIndex={disabled ? undefined : 0}
      className={cn(
        'relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 ps-8 pe-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:text-accent-foreground',
        disabled && 'pointer-events-none opacity-50',
        className,
      )}
      onClick={() => {
        if (disabled) return;
        onValueChange(itemValue);
        setOpen(false);
      }}
      {...props}
    >
      <span className="absolute start-2 flex h-3.5 w-3.5 items-center justify-center">
        {isSelected && <Check className="h-4 w-4" />}
      </span>
      {/* dir="auto" so mixed Hebrew/English option labels keep logical bidi order in RTL */}
      <span dir="auto">{children}</span>
    </div>
  );
});
SelectItem.displayName = 'SelectItem';

// Attach marker so scanForItems can identify SelectItem elements in the tree
(SelectItem as unknown as Record<string, unknown>).__SELECT_ITEM = SELECT_ITEM_TYPE;

/* ------------------------------------------------------------------ */
/*  Compat stubs                                                      */
/* ------------------------------------------------------------------ */

function SelectGroup({ children, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div role="group" {...props}>
      {children}
    </div>
  );
}

const SelectLabel = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('py-1.5 ps-8 pe-2 text-sm font-semibold', className)}
    {...props}
  />
));
SelectLabel.displayName = 'SelectLabel';

const SelectSeparator = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('-mx-1 my-1 h-px bg-muted', className)}
    {...props}
  />
));
SelectSeparator.displayName = 'SelectSeparator';

const SelectScrollUpButton = () => null;
const SelectScrollDownButton = () => null;

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
};
